// src/api/quiz.js
// Quiz API: data + helpers (Firestore-backed questions)
import { db } from "../config/firebase";
import { collection, getDocs, query } from "firebase/firestore";

// Local storage keys
const LS_XP_KEY = 'quiz_xp';
const LS_STATS_KEY = 'quiz_stats'; // { [discipline]: { attempted: number, correct: number } }

// Storage helpers
export function loadXp() {
  try {
    const v = localStorage.getItem(LS_XP_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
}

export function saveXp(xp) {
  try {
    localStorage.setItem(LS_XP_KEY, String(xp));
  } catch {}
}

export function loadStats() {
  try {
    const raw = localStorage.getItem(LS_STATS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveStats(stats) {
  try {
    localStorage.setItem(LS_STATS_KEY, JSON.stringify(stats));
  } catch {}
}

// Math helpers
export function pct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

export function getLevelFromXp(xp, levelThresholds) {
  if (!Array.isArray(levelThresholds) || levelThresholds.length === 0) return 1;
  const found = levelThresholds.find((t) => xp >= t.minXp && xp <= t.maxXp);
  return found ? found.level : levelThresholds[levelThresholds.length - 1].level;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Question selection
export function getRandomQuestions(allQuestions, discipline, level, count = 10) {
  if (!Array.isArray(allQuestions)) return [];
  const primary = allQuestions.filter(
    (q) => q.discipline === discipline && q.level === level
  );
  if (primary.length >= count) return shuffle(primary).slice(0, count);

  const poolMap = new Map(primary.map((q) => [q.id, q]));

  // Same discipline, any level
  allQuestions.filter((q) => q.discipline === discipline).forEach((q) => {
    if (!poolMap.has(q.id)) poolMap.set(q.id, q);
  });

  // Same level, any discipline
  if (poolMap.size < count) {
    allQuestions.filter((q) => q.level === level).forEach((q) => {
      if (!poolMap.has(q.id)) poolMap.set(q.id, q);
    });
  }

  // Fallback: whole bank
  if (poolMap.size < count) {
    allQuestions.forEach((q) => {
      if (!poolMap.has(q.id)) poolMap.set(q.id, q);
    });
  }

  return shuffle(Array.from(poolMap.values())).slice(0, count);
}

// =====================
// Quiz config (disciplines, levels) and Firestore fetch
// =====================

export const QUIZ_DISCIPLINES = [
  { id: 'fiqh', label: 'Fiqh (Jurisprudence)' },
  { id: 'aqida', label: 'Aqida (Croyance)' },
  { id: 'hadith', label: 'Hadith & Sunna' },
  { id: 'sirah', label: 'Sîrah (Vie du Prophète ﷺ)' }
];

export const QUIZ_LEVELS = [
  { id: 'easy', label: 'Facile' },
  { id: 'medium', label: 'Moyen' },
  { id: 'hard', label: 'Difficile' }
];

// Firestore-backed questions with simple in-memory cache
let QUESTIONS_CACHE = null;

/**
 * Fetch all quiz questions from Firestore (collection: quizz_questions).
 * Caches the result for the current session by default.
 */
export async function fetchAllQuizQuestions(force = false) {
  if (QUESTIONS_CACHE && !force) return QUESTIONS_CACHE;
  const colRef = collection(db, "quizz_questions");
  const snap = await getDocs(query(colRef));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  QUESTIONS_CACHE = items;
  return items;
}

/**
 * Return cached questions synchronously if already fetched; otherwise empty array.
 */
export function getCachedQuizQuestions() {
  return Array.isArray(QUESTIONS_CACHE) ? QUESTIONS_CACHE : [];
}

export const BASE_XP_PER_CORRECT = 10;
export const LEVEL_THRESHOLDS = [
  { level: 1, minXp: 0, maxXp: 99 },
  { level: 2, minXp: 100, maxXp: 249 },
  { level: 3, minXp: 250, maxXp: 499 },
  { level: 4, minXp: 500, maxXp: 799 },
  { level: 5, minXp: 800, maxXp: 1199 },
  { level: 6, minXp: 1200, maxXp: Infinity }
];
